<template>
    <div>
        <div class="vertical" style="height:120px; width:80vw;">
            <div :id="'scichart-root-' + sid" style="width: 100%; height: 100%; margin-right: 10px; align-self: center;"></div>
        </div>
    </div>
</template>

<script>
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { RubberBandXyZoomModifier } from "scichart/Charting/ChartModifiers/RubberBandXyZoomModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";
import { chartThemeMixin } from "@/utils/chartTheme";
import { NumberRange } from "scichart/Core/NumberRange";
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import { EXyDirection } from "scichart/types/XyDirection";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"
import { VerticalLineAnnotation } from "scichart/Charting/Visuals/Annotations/VerticalLineAnnotation";
import { ELabelPlacement } from "scichart/types/LabelPlacement";

import moment from 'moment'
import { LabelProvider } from 'scichart/Charting/Visuals/Axis/LabelProvider/LabelProvider';

export default {
    mixins: [chartThemeMixin],
    props: ['dataset', 'devices', 'day', 'name', 'activeSet', 'labelX', 'labelY', 'zoom', 'pan', 'showValues', 'mouseZoom', 'sid', 'initialZoomOut'],
    data() {
        var dateDay = new Date();
        dateDay.setDate(dateDay.getDate() - this.day)
        dateDay = new Date(dateDay.getFullYear(), dateDay.getMonth(), dateDay.getDate(), 0, 0, 0)

        return {
            sciChartSurface: null,
            wasmContext: null,
            init: false,
            data: [],
            dateFrom: dateDay,
            dateTo: new Date(dateDay.getFullYear(), dateDay.getMonth(), dateDay.getDate(), 23, 59, 59),
        }
    },
    watch: {
        dataset(newVal, oldVal) {
            if(newVal != 0) {
                this.changeDataSet(newVal, false)
            }
        },
    },
    methods: {
        async initSciChart() {
            const {sciChartSurface, wasmContext} = await SciChartSurface.create("scichart-root-" + this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            sciChartSurface.applyTheme(this.chartTheme())

            const xAxis = new NumericAxis(this.wasmContext, {
                axisAlignment: EAxisAlignment.Top,
                drawMajorBands: true,
                majorGridLineStyle: {
                    color: "#9D9D9D",
                },
            });

            const yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});
            yAxis.labelProvider.formatLabel = (dataValue) => parseInt(dataValue);
            yAxis.maxAutoTicks = 2;
            yAxis.minorsPerMajor = 2
            yAxis.visibleRange = new NumberRange(1, 2);

            if(this.initialZoomOut == true){
                yAxis.growBy = new NumberRange(0.02, 0.02);
                xAxis.growBy = new NumberRange(0.02, 0.02);
            }

            this.sciChartSurface.xAxes.add(xAxis)
            this.sciChartSurface.yAxes.add(yAxis)

            xAxis.labelProvider.formatLabel = (dt) => {
                return moment(dt).format("DD.MM, HH:mm:ss")
            }

            yAxis.labelProvider.formatLabel = (val) => {
                return this.name
            }

            sciChartSurface.annotations.add(
              new VerticalLineAnnotation({
                      labelPlacement: ELabelPlacement.Axis,
                      showLabel: true,
                      stroke: "Red",
                      strokeThickness: 2,
                      x1: Date.now(),
                      axisLabelFill: "Red",
                      labelValue: this.$t("Now")
                  }),
            );

            await this.populateSciChart(true)

            xAxis.autoTicks= false,
            xAxis.majorDelta= 3600000;
            xAxis.minorDelta= 720000;
            let lastRange = 3;

            xAxis.visibleRangeChanged.subscribe((args) => {
                let rangeDiff = args.visibleRange.max - args.visibleRange.min;

                if (rangeDiff/3600000 <= 1 && lastRange != 0) {
                  xAxis.majorDelta= 60000;
                  xAxis.minorDelta= 12000;
                  lastRange = 0;
                } else if (rangeDiff/3600000 <= 2 && rangeDiff/3600000 >= 1 && lastRange != 1) {
                  xAxis.majorDelta= 600000;
                  xAxis.minorDelta= 120000;
                  lastRange = 1;
                } else if ( rangeDiff / 3600000 > 2 && rangeDiff / 3600000 <= 6 && lastRange != 2) {
                  xAxis.majorDelta= 1800000;
                  xAxis.minorDelta= 360000;
                  lastRange = 2;
                } else if (rangeDiff / 3600000 > 6 && lastRange != 3) {
                  xAxis.majorDelta= 3600000;
                  xAxis.minorDelta= 720000;
                  lastRange = 3;
                }
            });
        },
        async reloadSciChart() {
            if(this.sciChartSurface != null) {
                if(this.sciChartSurface.renderableSeries != null){
                    this.sciChartSurface.renderableSeries.items = [];
                }

                await this.populateSciChart(false)
            }
        },
        populateSciChart(init) {
            this.data.forEach((e, i) => {
                var d = []
                var d2 = []
                var v = []
                var v2 = []
                var value = e.xValues.filter(x => !isNaN(x))[0]

                if(value == undefined){
                    value = parseInt(e.title)
                }

                //DF-1
                var c1 = new Date(this.dateFrom)
                c1.setSeconds(c1.getSeconds() - 1)
                d2.push(c1)
                if(e.xValues.length != 2){
                    d2.push(this.dateFrom)
                }
                //DTs
                e.yValues.forEach((dt, j) => {
                    d.push(new Date(moment(dt, "DD.MM.YYYY HH.mm.ss")))
                    d2.push(new Date(moment(dt, "DD.MM.YYYY HH.mm.ss")))
                })
                //DT+1
                if(e.xValues.length != 2){
                    d2.push(this.dateTo)
                }
                var e1 = new Date(this.dateTo)
                e1.setSeconds(e1.getSeconds() + 1)
                d2.push(e1)

                v2.push(NaN)
                v2.push(value)
                if(e.xValues.length != 2){
                    e.xValues.forEach((val, j) => {
                        v.push(val)
                        v2.push(!isNaN(val) ? NaN : value)
                    })
                }
                v2.push(value)
                v2.push(NaN)

                if(v.some(x => !isNaN(x))){
                    const dataSeries = new XyDataSeries(this.wasmContext, {
                        // SciChart przyjmuje na osi X tylko liczby - daty jako znaczniki czasu w ms.
                        xValues: d.map(x => new Date(x).getTime()),
                        yValues: v
                    })

                    dataSeries.dataSeriesName = e.title;

                    const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                        dataSeries,
                        drawNaNAs: ELineDrawMode.DiscontinuousLine,
                        stroke: '#E06C65',
                        strokeThickness: 15
                    })

                    lineSeries.strokeColor = '#E06C65'
                    lineSeries.rolloverModifierProps.tooltipTitle = e.title
                    lineSeries.rolloverModifierProps.tooltipTextColor = '#fff'
                    lineSeries.rolloverModifierProps.tooltipColor = e.strokeColor;
                    lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                    this.sciChartSurface.renderableSeries.add(lineSeries);
                }

                d2 = d2.sort((a,b) => new Date(a) - new Date(b))

                const greenDataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d2.map(x => new Date(x).getTime()),
                    yValues: v2
                })

                greenDataSeries.dataSeriesName = e.title;

                const lineSeries2 = new FastLineRenderableSeries(this.wasmContext, {
                    drawNaNAs: ELineDrawMode.DiscontinuousLine,
                    stroke: '#84CA85',
                    strokeThickness: 10
                })

                lineSeries2.dataSeries = greenDataSeries;

                this.sciChartSurface.renderableSeries.add(lineSeries2);
            })

            if(init) {
                if(this.zoomBySelection == true) this.sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({ xyDirection: EXyDirection.XDirection }));
                if(this.pan != false ) this.sciChartSurface.chartModifiers.add(new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }));
                if(this.zoom != false) this.sciChartSurface.chartModifiers.add(new ZoomExtentsModifier({ xyDirection: EXyDirection.XDirection }));
                if(this.mouseZoom != false) this.sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }));

                this.sciChartSurface.chartModifiers.add(new CursorModifier({
                    crosshairStroke: "#0095B2",
                    crosshairStrokeThickness: 1,
                    tooltipContainerBackground: "#000",
                    tooltipTextStroke: "#0095B2",
                    showTooltip: false,
                    axisLabelFill: "#0095B2",
                    axisLabelStroke: "#fff",
                }))
            }

            this.sciChartSurface.zoomExtents();
        },
        changeDataSet(index, init) {
            var set = [...this.dataset]

            //sort dataset
            set.sort((a, b) => new Date(a.createDate) - new Date(b.createDate))

            var dev = { xValues: [], yValues: [], title: -1 }

            set.forEach((e, i) => {
                var c1 = new Date(e.createDate)
                c1.setSeconds(c1.getSeconds() - 1)
                dev.yValues.push(c1)
                dev.xValues.push(NaN)

                dev.yValues.push(new Date(e.createDate))

                if(new Date(e.endDate) > this.dateTo){
                    dev.yValues.push(new Date(this.dateTo))
                    var e1 = new Date(this.dateTo)
                    e1.setSeconds(e1.getSeconds() + 1)
                    dev.yValues.push(e1)
                } else {
                    dev.yValues.push(new Date(e.endDate))
                    var e1 = new Date(e.endDate)
                    e1.setSeconds(e1.getSeconds() + 1)
                    dev.yValues.push(e1)
                }

                dev.xValues.push(1)
                dev.xValues.push(1)

                dev.xValues.push(NaN)
            })

            dev.title = 1

            this.data = [];
            this.data.push(dev)
            if(init == false) {
                this.reloadSciChart();
            }
        },
    },
    mounted: async function() {
        if(this.dataset.length == 0){
            await this.initSciChart();
            this.init = true;
            return;
        }
        this.changeDataSet(this.activeSet, true)
        await this.initSciChart();
        this.init = true;
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Charts/SciChartOneLineAlarms.vue